import * as amplitude from '@amplitude/analytics-react-native';
import { Platform } from 'react-native';

const AMPLITUDE_API_KEY = process.env.EXPO_PUBLIC_AMPLITUDE_API_KEY;

// Analytics désactivé en dev ET sur le web : sur navigateur le SDK se fait
// bloquer par les bloqueurs de pub (ERR_BLOCKED_BY_CLIENT) et inonde la console
// d'erreurs « Failed to fetch ». La version web ne sert qu'aux tests.
const ANALYTICS_DISABLED = __DEV__ || Platform.OS === 'web';

let initialized = false;

export async function initAnalytics() {
  if (initialized || ANALYTICS_DISABLED) return;
  if (!AMPLITUDE_API_KEY) return;
  try {
    // NB : le SDK RN v1 ne connaît pas `autocapture` (option du SDK web) —
    // seul le suivi de sessions est supporté nativement.
    await amplitude.init(AMPLITUDE_API_KEY, undefined, {
      trackingSessionEvents: true,
      // Par défaut le SDK transmet l'adresse IP, dont Amplitude déduit une
      // position approximative (pays / région / ville), plus les identifiants
      // publicitaires. L'app n'a aucun usage de tout ça : ce n'est pas
      // mentionné dans la politique de confidentialité, et le déclarer dans le
      // formulaire « Sécurité des données » de Google contredirait cette
      // politique. On coupe donc à la source plutôt que de déclarer une
      // collecte dont personne ne se sert.
      trackingOptions: {
        ipAddress: false,
        country: false,
        carrier: false,
        adid: false,
        idfv: false,
        appSetId: false,
      },
    }).promise;
    initialized = true;
  } catch {
    // Analytics init failed silently — non-blocking
  }
}

// --- User identity ---

export function identifyUser(userId: string, properties?: Record<string, any>) {
  if (ANALYTICS_DISABLED) return;
  amplitude.setUserId(userId);
  if (properties) {
    const identify = new amplitude.Identify();
    for (const [key, value] of Object.entries(properties)) {
      identify.set(key, value);
    }
    amplitude.identify(identify);
  }
}

export function resetUser() {
  if (ANALYTICS_DISABLED) return;
  amplitude.reset();
}

// --- Generic track ---

export function track(event: string, properties?: Record<string, any>) {
  if (ANALYTICS_DISABLED) return;
  amplitude.track(event, properties);
}

// --- Auth events ---

export function trackSignUp(method: string = 'email') {
  track('sign_up', { method });
}

export function trackLogin(method: string = 'email') {
  track('login', { method });
}

export function trackLogout() {
  track('logout');
  resetUser();
}

export function trackDeleteAccount() {
  track('delete_account');
  resetUser();
}

// --- Onboarding ---

export function trackOnboardingComplete() {
  track('onboarding_complete');
}

export function trackNotificationPermission(granted: boolean) {
  track('notification_permission', { granted });
}

export function trackRankingConsent(accepted: boolean) {
  track('ranking_consent', { accepted });
}

// --- Core features ---

export function trackDailyActionsCompleted(actionCount: number, totalPoints: number) {
  track('daily_actions_completed', { action_count: actionCount, total_points: totalPoints });
}

export function trackObjectiveSet(dailyTarget: number, actionCount: number) {
  track('objective_set', { daily_target: dailyTarget, action_count: actionCount });
}

export function trackQuizAnswered(isCorrect: boolean, points: number, dayOfWeek: number) {
  track('quiz_answered', { is_correct: isCorrect, points, day_of_week: dayOfWeek });
}

export function trackContestEntered() {
  track('contest_entered', {});
}

// --- Navigation / engagement ---

export function trackScreenView(screenName: string) {
  track('screen_view', { screen: screenName });
}

export function trackRankingTabChanged(tab: 'enterprise' | 'global') {
  track('ranking_tab_changed', { tab });
}

export function trackProfileEdited(fields: string[]) {
  track('profile_edited', { fields });
}

export function trackNotificationPreferenceChanged(type: string, enabled: boolean) {
  track('notification_preference_changed', { type, enabled });
}

export function trackExternalLinkOpened(url: string) {
  track('external_link_opened', { url });
}

export function trackBugReported() {
  track('bug_reported');
}
